import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Camera, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import TimeInput from '../../components/ui/TimeInput';
import Button from '../../components/ui/Button';
import Alert from '../../components/ui/Alert';
import CalendarPicker from '../../components/ui/CalendarPicker';
import Loading from '../../components/ui/Loading';
import { hourmeterService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const LOW_HOURS_THRESHOLD = 5;

/**
 * `horasTrabajo` es un string derivado: queda en `''` mientras no haya lecturas
 * válidas. `Number('')` es 0, así que leerlo crudo haría pasar el formulario
 * vacío por "cero horas" y abriría el modal apenas se entra a la pantalla.
 */
const parseWorkedHours = (raw: string): number => (raw.trim() === '' ? NaN : Number(raw));

/**
 * Cuándo pedir motivo por la jornada.
 *
 * El guard era `hours > 0 && hours <= 5`. Ese `> 0` estaba para no abrir el
 * modal con el campo vacío, y de paso se llevó el caso de cero horas — el que
 * más hay que explicar. Vacío es NaN, no cero.
 *
 * El motivo admite texto libre también en cero: la lista son los seis que el
 * backend sabe interpretar para decidir el standby, pero la realidad de la obra
 * no cabe en seis opciones.
 */
const needsLowHoursReason = (hoursWorked: number): boolean => {
  if (!Number.isFinite(hoursWorked) || hoursWorked < 0) return false;
  return hoursWorked <= LOW_HOURS_THRESHOLD;
};

const LOW_HOURS_REASONS = [
  'Sin trabajo en obra',
  'Falla mecánica',
  'Pinchazo/problema de llantas',
  'Mantenimiento',
  'Clima adverso',
  'Falta combustible',
] as const;

interface HourmeterFormData {
  date: string;
  initialHour: string;
  finalHour: string;
  horasTrabajo: string;
  standby: boolean;
  accesorio: boolean;
  horasTrabajoAccesorio: number;
  imagenes: File[];
  enterHour: string;
  outHour: string;
  observaciones: string;
}

interface CreateHourmeterLocationState {
  dealId?: number;
  operatorId?: number;
}

interface DealResidentInfo {
  residentId: number;
  name: string;
  phone?: string;
  email?: string;
}

const parseHour = (value: string) => Number(value.replace(',', '.'));

const getNormalizedDateString = (date: Date) => {
  const timezoneOffsetInMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetInMs).toISOString().split('T')[0];
};

const getNextDateString = (dateString: string): string => {
  const baseDate = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(baseDate.getTime())) {
    return getNormalizedDateString(new Date());
  }
  baseDate.setDate(baseDate.getDate() + 1);
  return getNormalizedDateString(baseDate);
};

const createInitialFormData = (dateOverride?: string): HourmeterFormData => ({
  date: dateOverride ?? getNormalizedDateString(new Date()),
  initialHour: '',
  finalHour: '',
  horasTrabajo: '',
  standby: false,
  accesorio: false,
  horasTrabajoAccesorio: 0,
  imagenes: [],
  enterHour: '',
  outHour: '',
  observaciones: '',
});

const CreateHourmeter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as CreateHourmeterLocationState) || {};
  const [dealId, setDealId] = useState<number | null>(locationState.dealId ?? null);
  const [operatorId, setOperatorId] = useState<number | null>(locationState.operatorId ?? null);
  const [formData, setFormData] = useState<HourmeterFormData>(() => createInitialFormData());
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [haveAccessorie, setHaveAccessorie] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const { operator } = useAuth();
  const [dealStatus, setDealStatus] = useState<'idle' | 'loading' | 'error'>(dealId ? 'idle' : 'loading');
  const [dealMessage, setDealMessage] = useState('');
  const [isDealErrorModalOpen, setDealErrorModalOpen] = useState(false);
  const [formResetVersion, setFormResetVersion] = useState(0);
  const [lastFinalHour, setLastFinalHour] = useState('');
  const [prefillInitialHour, setPrefillInitialHour] = useState<string | null>(null);
  const [lastSubmittedDate, setLastSubmittedDate] = useState<string | null>(null);
  const isFormDisabled = dealStatus === 'loading' || isSubmitting;
  const [lastDate, setLastDate] = useState<string | null>(null);
  const [hasMultipleResidents, setHasMultipleResidents] = useState(false);
  const [residents, setResidents] = useState<DealResidentInfo[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState<number | null>(null);
  const [isResidentModalOpen, setResidentModalOpen] = useState(false);
  const [residentSelectionError, setResidentSelectionError] = useState('');
  const [hourmeterDates, sethourmeterDates] = useState<Set<string>>(new Set());
  const [isLowHoursModalOpen, setLowHoursModalOpen] = useState(false);
  const [lowHoursReason, setLowHoursReason] = useState<string | null>(null);
  const [lowHoursCustomReason, setLowHoursCustomReason] = useState('');
  const [lowHoursError, setLowHoursError] = useState('');
  const [pendingResidentId, setPendingResidentId] = useState<number | null>(null);
  const [imageError, setImageError] = useState('');
  const [enterHourError, setEnterHourError] = useState('');
  const [outHourError, setOutHourError] = useState('');
  const isSubmitModalVisible = submitStatus !== 'idle' && Boolean(submitMessage);
  const todayString = getNormalizedDateString(new Date());

  const hourmeterError = (() => {
    const initial = parseHour(formData.initialHour);
    const final = parseHour(formData.finalHour);
    if (!formData.initialHour || !formData.finalHour) return '';
    if (Number.isNaN(initial) || Number.isNaN(final)) return '';
    if (final <= initial) return 'El horómetro final debe ser mayor al inicial.';
    if (final - initial > 24) return 'El horómetro no puede exceder 24 horas de diferencia.';
    return '';
  })();

  const handleDealModalClose = () => {
    setDealErrorModalOpen(false);
    navigate('/dashboard', { replace: true });
  };

  const handleChange = (field: keyof HourmeterFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = event.target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
    const value = isCheckbox ? target.checked : target.value;

    setFormData((prev) => {
      if (field === 'accesorio' && haveAccessorie) {
        return prev;
      }
      const updated: HourmeterFormData = {
        ...prev,
        [field]: value as HourmeterFormData[typeof field],
      };

      if (field === 'accesorio' && value === false) {
        updated.horasTrabajoAccesorio = 0;
      }

      return updated;
    });
  };

  const handleImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setFormData((prev) => ({
      ...prev,
      imagenes: files,
    }));
    if (files.length > 0) {
      setImageError('');
    }
  };

  useEffect(() => {
    if (formData.imagenes.length === 0) {
      setImagePreviews([]);
      return;
    }

    const urls = formData.imagenes.map((file) => URL.createObjectURL(file));
    setImagePreviews(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [formData.imagenes]);

  useEffect(() => {
    if (operator?.id && operatorId !== operator.id) {
      setOperatorId(operator.id);
    }
  }, [operator, operatorId]);

  useEffect(() => {
    const { initialHour, finalHour } = formData;

    if (!initialHour || !finalHour) {
      setFormData((prev) => (prev.horasTrabajo === '' ? prev : { ...prev, horasTrabajo: '' }));
      return;
    }

    const parsedInitial = parseHour(initialHour);
    const parsedFinal = parseHour(finalHour);

    if (Number.isNaN(parsedInitial) || Number.isNaN(parsedFinal)) {
      return;
    }

    const rawDiff = parsedFinal - parsedInitial;
    const roundedDiff = Math.round(rawDiff * 100) / 100;
    const calculatedHours = Number.isFinite(roundedDiff) ? roundedDiff.toString() : '';

    if (Number(calculatedHours) > 0) {
      setFormData((prev) => (prev.horasTrabajo === calculatedHours ? prev : { ...prev, horasTrabajo: calculatedHours }));
    } else {
      setFormData((prev) => (prev.horasTrabajo === '' ? prev : { ...prev, horasTrabajo: '' }));
    }
  }, [formData.initialHour, formData.finalHour]);

  useEffect(() => {
    let isMounted = true;

    if (!operator?.id) {
      return;
    }

    if (dealId && lastDate) {
      return () => {
        isMounted = false;
      };
    }

    if (!dealId) {
      setDealStatus('loading');
      setDealMessage('');
    }

    hourmeterService
      .getActiveDealForOperator(operator.id)
      .then((response) => {
        if (!isMounted) return;
        const fetchedDealId = response?.dealId ?? null;

        if (!fetchedDealId) {
          const message = 'No se encontró una oferta activa.';
          setDealStatus('error');
          setDealMessage(message);
          setDealErrorModalOpen(true);
          return;
        }

        const normalizedDate = response?.lastDate
          ? new Date(response.lastDate).toISOString().split('T')[0]
          : null;
        const normalizedLastFinalHour =
          response?.lastFinalHour !== undefined && response?.lastFinalHour !== null
            ? String(response.lastFinalHour)
            : '';

        setDealId(fetchedDealId);
        setLastDate(normalizedDate);
        setLastFinalHour(normalizedLastFinalHour);
        setPrefillInitialHour(normalizedLastFinalHour || null);
        setOperatorId(operator.id);
        setDealStatus('idle');
        setHaveAccessorie(response?.haveAccessorie || false);
        const fetchedResidents = response?.residents ?? [];
        const multipleResidents = Boolean(response?.hasMultipleResidents);
        setResidents(fetchedResidents);
        setHasMultipleResidents(multipleResidents);
        setSelectedResidentId(multipleResidents ? null : fetchedResidents[0]?.residentId ?? null);
        const rawDates = response?.hourmeterDates ?? [];
        sethourmeterDates(new Set(rawDates.map((d: string) => d.split('T')[0])));
      })
      .catch((error: any) => {
        if (!isMounted) return;
        console.error('Error fetching deal in CreateHourmeter:', error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'No se pudo obtener la oferta activa.';
        setDealStatus('error');
        setDealMessage(message);
        setDealErrorModalOpen(true);
      });

    return () => {
      isMounted = false;
    };
  }, [dealId, lastDate, operator]);

  useEffect(() => {
    if (!lastDate) return;

    const nextDate = getNextDateString(lastDate);

    setFormData((prev) => {
      if (prev.date === nextDate) return prev;
      return { ...prev, date: nextDate };
    });
  }, [lastDate]);

  useEffect(() => {
    if (!prefillInitialHour) return;

    setFormData((prev) => {
      if (prev.initialHour) {
        return prev;
      }
      return { ...prev, initialHour: prefillInitialHour };
    });

    setPrefillInitialHour(null);
  }, [prefillInitialHour]);

  useEffect(() => {
    setFormData((prev) => {
      if (prev.accesorio === haveAccessorie) {
        return prev;
      }
      return { ...prev, accesorio: haveAccessorie };
    });
  }, [haveAccessorie]);

  const validateForm = () => {
    let isValid = true;

    if (formData.imagenes.length === 0) {
      setImageError('Debes agregar al menos una foto del horómetro.');
      isValid = false;
    }

    if (!formData.enterHour) {
      setEnterHourError('Selecciona la hora de entrada.');
      isValid = false;
    }

    if (!formData.outHour) {
      setOutHourError('Selecciona la hora de salida.');
      isValid = false;
    }

    if (!isValid) return false;

    if (!dealId || !operatorId) {
      if (!dealMessage) {
        setDealMessage('No se encontró una oferta activa. Contacta al administrador de RETRI para que se te asigne una.');
      }
      setDealErrorModalOpen(true);
      setSubmitStatus('error');
      setSubmitMessage('No se encontró la oferta activa o el operador. Inténtalo nuevamente.');
      return false;
    }

    const initialHourValue = parseHour(formData.initialHour);
    const finalHourValue = parseHour(formData.finalHour);

    if (Number.isNaN(initialHourValue) || Number.isNaN(finalHourValue)) {
      setSubmitStatus('error');
      setSubmitMessage('Los valores de horómetro deben ser numéricos.');
      return false;
    }

    if (finalHourValue < initialHourValue) {
      setSubmitStatus('error');
      setSubmitMessage('El horómetro final no puede ser menor al horómetro inicial.');
      return false;
    }

    if (finalHourValue - initialHourValue > 24) {
      setSubmitStatus('error');
      setSubmitMessage('El horómetro no puede exceder 24 horas de diferencia.');
      return false;
    }

    return true;
  };

  const resolveResidentId = () => {
    if (hasMultipleResidents) {
      if (residents.length === 0) {
        setSubmitStatus('error');
        setSubmitMessage('No se encontraron residentes para notificar la firma.');
        return null;
      }
      if (!selectedResidentId) {
        setResidentSelectionError('');
        setResidentModalOpen(true);
        return null;
      }
      return selectedResidentId;
    }

    const defaultResidentId = residents[0]?.residentId ?? null;
    if (!defaultResidentId) {
      setSubmitStatus('error');
      setSubmitMessage('No se encontró un residente para notificar la firma.');
      return null;
    }
    return defaultResidentId;
  };

  const submitHourmeter = async (residentId: number, reason?: string) => {
    const initialHourValue = parseHour(formData.initialHour);
    const finalHourValue = parseHour(formData.finalHour);
    const submittedFinalHour = formData.finalHour;
    const startDate = formData.enterHour
      ? `${formData.date}T${formData.enterHour}`
      : undefined;
    const finalDate = formData.outHour
      ? `${formData.date}T${formData.outHour}`
      : undefined;

    setIsSubmitting(true);

    try {
      const response = await hourmeterService.createHourmeterOperator(
        {
          dealId: dealId!,
          operatorId: operatorId!,
          residentId: residentId,
          initialHour: initialHourValue,
          finalHour: finalHourValue,
          date: formData.date,
          startDate,
          finalDate,
          standby: formData.standby,
          accesorio: formData.accesorio,
          horasTrabajoAccesorio: formData.horasTrabajoAccesorio,
          operatorObservations: formData.observaciones,
          novelty: reason,
        },
        formData.imagenes
      );

      setSubmitStatus('success');
      setSubmitMessage(
        response?.message || 'Horómetro creado correctamente.'
      );

      sethourmeterDates((prev) => new Set(prev).add(formData.date));
      setLastFinalHour(submittedFinalHour);
      setLastSubmittedDate(formData.date);
      resetForm(formData.date);
    } catch (error: any) {
      console.error('Error creating hourmeter:', error);
      const apiMessage =
        error?.response?.data?.message || 'No se pudo crear el horómetro. Inténtalo nuevamente.';
      setSubmitStatus('error');
      setSubmitMessage(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const proceedToSubmit = async (residentId: number, reason?: string) => {
    const hours = parseWorkedHours(formData.horasTrabajo);
    if (needsLowHoursReason(hours) && !reason) {
      setPendingResidentId(residentId);
      setLowHoursReason(null);
      setLowHoursCustomReason('');
      setLowHoursError('');
      setLowHoursModalOpen(true);
      return;
    }
    await submitHourmeter(residentId, reason);
  };

  const handleConfirmLowHours = async () => {
    const isCustom = lowHoursReason === 'Otro motivo (especificar)';
    const finalReason = isCustom ? lowHoursCustomReason.trim() : lowHoursReason;

    if (!finalReason) {
      setLowHoursError('Selecciona una razón para continuar.');
      return;
    }

    setLowHoursModalOpen(false);
    setLowHoursError('');

    if (pendingResidentId) {
      await submitHourmeter(pendingResidentId, finalReason);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitStatus('idle');
    setSubmitMessage('');

    if (!validateForm()) {
      return;
    }

    const residentId = resolveResidentId();
    if (!residentId) {
      return;
    }

    await proceedToSubmit(residentId);
  };

  const handleConfirmResident = async () => {
    if (!selectedResidentId) {
      setResidentSelectionError('Selecciona un residente para continuar.');
      return;
    }

    setResidentModalOpen(false);
    setResidentSelectionError('');
    setSubmitStatus('idle');
    setSubmitMessage('');

    if (!validateForm()) {
      return;
    }

    await proceedToSubmit(selectedResidentId);
  };

  const resetForm = (dateToUse?: string) => {
    const nextDate = dateToUse ?? getNormalizedDateString(new Date());
    setFormData(createInitialFormData(nextDate));
    setImagePreviews([]);
    setFormResetVersion((prev) => prev + 1);
  };

  const handleCloseSubmitModal = () => {
    setSubmitStatus('idle');
    setSubmitMessage('');
  };

  const handleContinueRegistration = () => {
    handleCloseSubmitModal();
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    const baseDateString = lastSubmittedDate || formData.date;
    const dateForIncrement = baseDateString ? new Date(`${baseDateString}T00:00:00`) : new Date();
    dateForIncrement.setDate(dateForIncrement.getDate() + 1);
    const nextDate = getNormalizedDateString(dateForIncrement);

    setFormData((prev) => ({
      ...prev,
      date: nextDate,
      initialHour: lastFinalHour,
      finalHour: '',
      horasTrabajo: '',
      accesorio: haveAccessorie,
    }));
  };

  const handleGoBackToHourmeters = () => {
    handleCloseSubmitModal();
    navigate('/dashboard');
  };

  return (
    <>
      {isResidentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">Selecciona residente</h2>
            <p className="mt-2 text-sm text-gray-600">
              Indica a quién se debe notificar para la firma del horómetro.
            </p>
            <div className="mt-4 space-y-3">
              {residents.map((resident) => (
                <label
                  key={resident.residentId}
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 px-3 py-2 hover:border-retri-blue"
                >
                  <input
                    type="radio"
                    name="resident"
                    value={resident.residentId}
                    checked={selectedResidentId === resident.residentId}
                    onChange={() => setSelectedResidentId(resident.residentId)}
                    className="mt-1 h-4 w-4 text-retri-blue focus:ring-retri-blue"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{resident.name}</span>
                    {resident.phone && (
                      <span className="text-xs text-gray-500">{resident.phone}</span>
                    )}
                    {resident.email && (
                      <span className="text-xs text-gray-500">{resident.email}</span>
                    )}
                  </div>
                </label>
              ))}
            </div>
            {residentSelectionError && (
              <p className="mt-3 text-sm text-red-600">{residentSelectionError}</p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setResidentModalOpen(false);
                  setResidentSelectionError('');
                }}
              >
                Cancelar
              </Button>
              <Button type="button" onClick={handleConfirmResident}>
                Continuar
              </Button>
            </div>
          </div>
        </div>
      )}
      {isLowHoursModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Horas trabajadas menores a 5</h2>
              <button
                type="button"
                onClick={() => setLowHoursModalOpen(false)}
                className="rounded-md p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {parseWorkedHours(formData.horasTrabajo) === 0
                ? 'El horómetro no se movió. Selecciona el motivo para dejarlo reportado.'
                : 'Por favor, selecciona una razón para que la máquina trabajara menos de 5 horas.'}
            </p>
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-900">
                {parseWorkedHours(formData.horasTrabajo) === 0
                  ? '¿Por qué la máquina no trabajó?'
                  : '¿Por qué la máquina trabajó menos de 5 horas?'}{' '}
                <span className="text-red-500 text-xs font-semibold">(OBLIGATORIO)</span>
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {LOW_HOURS_REASONS.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => { setLowHoursReason(reason); setLowHoursError(''); }}
                    className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                      lowHoursReason === reason
                        ? 'border-retri-blue bg-blue-50 font-medium text-retri-blue'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => { setLowHoursReason('Otro motivo (especificar)'); setLowHoursError(''); }}
                className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                  lowHoursReason === 'Otro motivo (especificar)'
                    ? 'border-retri-blue bg-blue-50 font-medium text-retri-blue'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
              >
                ✏️ Otro motivo (especificar)
              </button>
              {lowHoursReason === 'Otro motivo (especificar)' && (
                <input
                  type="text"
                  value={lowHoursCustomReason}
                  onChange={(e) => { setLowHoursCustomReason(e.target.value); setLowHoursError(''); }}
                  placeholder="Escribe el motivo"
                  className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2"
                />
              )}
            </div>
            {lowHoursError && (
              <p className="mt-3 text-sm text-red-600">{lowHoursError}</p>
            )}
            <div className="mt-6 flex justify-end">
              <Button type="button" onClick={handleConfirmLowHours}>
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}
      {isSubmitModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md">
            <Alert
              variant={submitStatus === 'error' ? 'error' : 'success'}
              title={submitStatus === 'error' ? 'No se pudo crear el horómetro' : 'Horómetro creado'}
              className="shadow-2xl"
            >
              <div className="space-y-4">
                <p>{submitMessage}</p>
                {submitStatus === 'success' ? (
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      type="button"
                      variant="primary"
                      className="w-full"
                      onClick={handleContinueRegistration}
                    >
                      Seguir registrando
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleGoBackToHourmeters}
                    >
                      Volver a horómetros
                    </Button>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <Button type="button" variant="primary" onClick={handleCloseSubmitModal}>
                      Entendido
                    </Button>
                  </div>
                )}
              </div>
            </Alert>
          </div>
        </div>
      )}
      <div className="min-h-screen bg-gray-200">
        {/* Header */}
        <header className="bg-retri-blue shadow-md">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:text-blue-200 text-sm font-medium"
            >
              ← Volver
            </button>
            <div className="h-4 w-px bg-blue-300" />
            <h1 className="text-white font-bold text-base">Crear horómetro</h1>
          </div>
        </header>

        <div className="py-6 px-4 sm:px-8">
      <div className="max-w-lg mx-auto">

        {dealStatus === 'loading' ? (
          <div className="flex items-center justify-center py-20">
            <Loading size="lg" text="Cargando datos..." />
          </div>
        ) : (
        <Card>
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl text-gray-900">Crear horómetro</CardTitle>
            <CardDescription>
              Completa la información para registrar un nuevo horómetro en el sistema.
            </CardDescription>
          </CardHeader>

          <form key={formResetVersion} onSubmit={handleSubmit}>
            <CardContent className="pt-6 space-y-6">
              {dealStatus === 'error' && dealMessage && (
                <Alert variant="error" title="No se pudo obtener la oferta activa">
                  {dealMessage}
                </Alert>
              )}

              {/* Row 1: Fecha + Calendario */}
              <CalendarPicker
                selectedDate={formData.date}
                onChange={(date) => setFormData((prev) => ({ ...prev, date }))}
                hourmeterDates={hourmeterDates}
                maxDate={todayString}
                disabled={isFormDisabled}
                defaultOpen
              />

              {/* Row 2: Horómetro inicial + Horómetro final */}
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  inputMode="decimal"
                  label="Horómetro inicial"
                  placeholder="Ej: 1200.5"
                  value={formData.initialHour}
                  onChange={handleChange('initialHour')}
                  required
                  disabled={isFormDisabled}
                />
                <Input
                  type="text"
                  inputMode="decimal"
                  label="Horómetro final"
                  placeholder="Ej: 1215.3"
                  value={formData.finalHour}
                  onChange={handleChange('finalHour')}
                  required
                  disabled={isFormDisabled}
                  error={hourmeterError}
                  className={hourmeterError ? 'bg-red-50' : ''}
                />
              </div>

              {/* Row 3: Horas trabajadas + Standby */}
              <div className="rounded-lg border-2 border-gray-400 bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">Horas trabajadas</span>
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                    {formData.horasTrabajo || '—'}
                    {formData.horasTrabajo && Number(formData.horasTrabajo) <= 5 && (
                      <AlertCircle className="h-4 w-4" style={{ color: '#d08700' }} />
                    )}
                  </span>
                </div>
                <hr className="my-3 border-gray-100" />
                <div className="flex items-center space-x-3">
                  <input
                    id="standby"
                    type="checkbox"
                    className="h-4 w-4 text-retri-blue focus:ring-retri-blue border-gray-300 rounded"
                    checked={formData.standby}
                    onChange={handleChange('standby')}
                    disabled={isFormDisabled}
                  />
                  <label htmlFor="standby" className="text-sm font-semibold text-gray-800">
                    ¿Stand by?
                  </label>
                </div>
              </div>

              {/* Row 4: Accesorio */}
              <div className="flex items-center space-x-3">
                <input
                  id="accesorio"
                  type="checkbox"
                  className="h-4 w-4 text-retri-blue focus:ring-retri-blue border-gray-300 rounded"
                  checked={formData.accesorio}
                  onChange={handleChange('accesorio')}
                  disabled={isFormDisabled || haveAccessorie}
                />
                <label htmlFor="accesorio" className="text-sm font-semibold text-gray-800">
                  ¿Usaste algún accesorio?
                </label>
              </div>

              {formData.accesorio && (
                <Input
                  type="number"
                  label="Horas Accesorio"
                  placeholder="Ej: 8"
                  value={formData.horasTrabajoAccesorio}
                  onChange={handleChange('horasTrabajoAccesorio')}
                  min="0"
                  step="0.1"
                  required
                  disabled={isFormDisabled}
                />
              )}

              {/* Row 5: Hora de entrada */}
              <TimeInput
                label="Hora de entrada"
                value={formData.enterHour}
                onChange={(value) => { setFormData(prev => ({ ...prev, enterHour: value })); setEnterHourError(''); }}
                disabled={isFormDisabled}
                defaultScrollTo="07:00"
                error={enterHourError}
                required
              />

              {/* Row 6: Hora de salida */}
              <TimeInput
                label="Hora de salida"
                value={formData.outHour}
                onChange={(value) => { setFormData(prev => ({ ...prev, outHour: value })); setOutHourError(''); }}
                disabled={isFormDisabled}
                defaultScrollTo="18:00"
                error={outHourError}
                required
              />

              {/* Row 7: Imágenes de horómetros */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-800">
                  Imágenes de horómetros<span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  id="signedDocImages"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  disabled={isFormDisabled}
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={isFormDisabled}
                  onClick={() => document.getElementById('signedDocImages')?.click()}
                  className={`flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${imageError ? 'border-red-400 bg-red-50 text-red-500 hover:border-red-500 hover:text-red-600' : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-700'}`}
                >
                  <Camera className="h-4 w-4" />
                  Elegir archivos
                </button>
                {imageError && (
                  <p className="text-sm text-red-600">{imageError}</p>
                )}
                {formData.imagenes.length > 0 && (
                  <div className="mt-1 space-y-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
                    <p className="text-xs font-medium text-gray-600">
                      {formData.imagenes.length} archivo{formData.imagenes.length > 1 ? 's' : ''} seleccionado{formData.imagenes.length > 1 ? 's' : ''}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {formData.imagenes.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="flex flex-col items-center gap-2">
                          {imagePreviews[index] && (
                            <img
                              src={imagePreviews[index]}
                              alt={`Vista previa ${file.name}`}
                              className="h-24 w-full rounded-md object-cover shadow-sm"
                            />
                          )}
                          <span className="truncate text-xs text-gray-700 w-full text-center">
                            {file.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Row 8: Actividad realizada */}
              <div className="flex flex-col">
                <label htmlFor="observaciones" className="text-sm font-semibold text-gray-800 mb-2">
                  Actividad realizada (opcional)
                </label>
                <textarea
                  id="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange('observaciones')}
                  placeholder="Describe la actividad realizada"
                  disabled={isFormDisabled}
                  className="min-h-[120px] rounded-md border-2 border-gray-400 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-500 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </CardContent>

            <CardFooter className="justify-end gap-3">
              <Button type="button" variant="ghost" onClick={() => navigate('/dashboard')} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting || isFormDisabled}>
                {isSubmitting ? 'Guardando...' : 'Guardar horómetro'}
              </Button>
            </CardFooter>
          </form>
        </Card>
        )}
      </div>
      </div>
      {isDealErrorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Sin negocio asignado</h2>
            <p className="text-sm text-gray-700 mb-6">
              No tienes un negocio asignado actualmente. Comunícate con el administrador de RETRI para que te asignen uno antes de registrar un horómetro.
            </p>
            <div className="flex justify-end">
              <Button onClick={handleDealModalClose}>Entendido</Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CreateHourmeter;
